import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Card,
  Form,
  Switch,
  Button,
  message,
  Space,
  Alert,
  Divider,
  Row,
  Col,
  Select,
  Grid
} from 'antd';
import {
  ApiOutlined,
  SaveOutlined
} from '@ant-design/icons';
import cdnService from '../../services/cdnService';
import { DurationInput } from '../../../shared/components/cdn/UnitInputs';
import { UpgradeTag, fieldLocked } from '../../../shared/components/cdn/PlanGate';

const { Option } = Select;
const { useBreakpoint } = Grid;

export default function Network() {
  const { t } = useTranslation();
  const { domainData, reloadDomain } = useOutletContext();
  // 回源加速(分层加速)属 full 缓存级别
  const l2Locked = fieldLocked(domainData, 'l2_policy');
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (domainData) {
      form.setFieldsValue({
        websocket_enabled: !!domainData.websocket_enabled,
        proxy_timeout: domainData.proxy_timeout || 60,
        max_body_size: domainData.max_body_size ?? 0,
        http_version: domainData.http_version || '1.1',
        keep_alive: domainData.keep_alive !== undefined ? domainData.keep_alive : true,
        keepalive_timeout: domainData.keepalive_timeout || 60,
        l2_on: domainData.l2_policy === 'on' || domainData.l2_policy === 'force_l2'
      });
    }
  }, [domainData, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const response = await cdnService.updateDomain({
        id: domainData.id,
        websocket_enabled: !!values.websocket_enabled,
        proxy_timeout: values.proxy_timeout,
        max_body_size: values.max_body_size,
        http_version: values.http_version,
        keep_alive: values.keep_alive,
        keepalive_timeout: values.keepalive_timeout,
        ...(domainData.l2_available ? { l2_policy: values.l2_on ? 'on' : 'auto' } : {})
      });

      if (response.success) {
        message.success(t('Saved successfully'));
        reloadDomain();
      } else {
        message.error(response.error || t('Failed to save'));
      }
    } catch (error) {
      message.error(t('Failed to save'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: isMobile ? '12px 0' : 0 }}>
      <Card
        title={
          <Space>
            <ApiOutlined />{t('Network settings')}</Space>
        }
        extra={
          !isMobile && (
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSubmit}
              loading={loading}
            >{t('Save settings')}</Button>
          )
        }
        size={isMobile ? 'small' : 'default'}
      >
        {isMobile && (
          <div style={{ marginBottom: 16 }}>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSubmit}
              loading={loading}
              size="small"
              block
            >{t('Save settings')}</Button>
          </div>
        )}

        <Alert
          message={t('Network configuration')}
          description={isMobile ? t('Configure network connections between the CDN and origin') : t('Configure network connections between the CDN and origin, and client connection settings.')}
          type="info"
          showIcon
          style={{ marginBottom: isMobile ? 16 : 24 }}
        />

        <Form
          form={form}
          layout="vertical"
        >
          {domainData?.l2_available && (
            <>
          <Divider orientation="left" style={{ margin: isMobile ? '0 0 12px' : '0 0 24px' }}>{t('Origin routing')}</Divider>

          <Form.Item
            name="l2_on"
            label={<span>{t('Origin acceleration')}{l2Locked && <UpgradeTag need="full" />}</span>}
            valuePropName="checked"
            tooltip={t('Fetch from the origin via an acceleration node close to it — useful when the origin is far from visitors. Billed by traffic through each node; falls back to direct when unavailable.')}
          >
            <Switch disabled={l2Locked && !(domainData.l2_policy === 'on' || domainData.l2_policy === 'force_l2')} />
          </Form.Item>
            </>
          )}

          <Divider orientation="left" style={{ margin: isMobile ? '12px 0' : '24px 0' }}>{t('Origin protocol')}</Divider>

          <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]}>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label={t('Origin HTTP version')}
                name="http_version"
                tooltip={!isMobile ? t('HTTP version used for CDN origin requests') : undefined}
              >
                <Select>
                  <Option value="1.0">HTTP/1.0</Option>
                  <Option value="1.1">HTTP/1.1</Option>
                  <Option value="2">HTTP/2</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={12} sm={12} md={8}>
              <Form.Item
                label="WebSocket"
                name="websocket_enabled"
                valuePropName="checked"
                tooltip={!isMobile ? t('Let visitors open WebSocket connections to your origin through the CDN') : undefined}
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" style={{ margin: isMobile ? '12px 0' : '24px 0' }}>{t('Connection settings')}</Divider>

          <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]}>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label={isMobile ? t('Timeout') : t('Proxy timeout')}
                name="proxy_timeout"
                tooltip={!isMobile ? t('CDN-to-origin connection timeout. A longer timeout is recommended for WebSocket.') : undefined}
                rules={[
                  { required: true, message: t('Enter the timeout') },
                  { type: 'number', min: 1, max: 3600, message: t('Range: 1-3600 seconds') }
                ]}
              >
                <DurationInput presets={[10, 30, 60, 120, 300, 600]} min={1} max={3600} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={12} md={8}>
              <Form.Item
                label="Keep-Alive"
                name="keep_alive"
                valuePropName="checked"
                tooltip={!isMobile ? t('Enable HTTP Keep-Alive to reuse TCP connections and improve performance') : undefined}
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label={isMobile ? t('Maximum request body (MB)') : t('Maximum request body size (MB)')}
                name="max_body_size"
                tooltip={!isMobile ? t('Limit client upload/request body size. 0 = unlimited (still subject to the node\'s hard limit). Requests over the limit return 413.') : undefined}
              >
                <Select>
                  <Option value={0}>{t('Unlimited')}</Option>
                  <Option value={10}>10 MB</Option>
                  <Option value={50}>50 MB</Option>
                  <Option value={100}>100 MB</Option>
                  <Option value={200}>200 MB</Option>
                  <Option value={500}>500 MB</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.keep_alive !== cur.keep_alive}>
            {({ getFieldValue }) =>
              getFieldValue('keep_alive') ? (
                <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]}>
                  <Col xs={12} sm={12} md={8}>
                    <Form.Item
                      label={isMobile ? t('KA timeout') : t('Keep-Alive timeout')}
                      name="keepalive_timeout"
                      tooltip={!isMobile ? t('How long idle connections are kept open before being closed to free resources') : undefined}
                      rules={[
                        { type: 'number', min: 1, max: 3600, message: t('Range: 1-3600 seconds') }
                      ]}
                    >
                      <DurationInput presets={[15, 30, 60, 120, 300]} min={1} max={3600} />
                    </Form.Item>
                  </Col>
                </Row>
              ) : null
            }
          </Form.Item>


          <Alert
            message={t('Notice')}
            description={isMobile ? t('For WebSocket, set the proxy timeout to 300 seconds or longer') : t('Changes to network settings take effect within a few minutes. If you enable WebSocket, we recommend setting the proxy timeout to 300 seconds or longer.')}
            type="warning"
            showIcon
            style={{ marginTop: isMobile ? 16 : 24 }}
          />
        </Form>
      </Card>
    </div>
  );
}
